

import { Component, Vue, Watch, Prop, ProvideReactive } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"
import { treeFilter } from "@/mixins/treeFilter"

// @ts-ignore
import cloneDeep from "lodash/cloneDeep"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"
import DataModal from "../DataModal.vue"
import DataDetailsModal from "@/components/CaseDetails/DataDetailsModal.vue"
import UserImage from "../../UserImage.vue"
import DataSourceSelect from "@/components/Fields/DataSourceSelect.vue"
import SkeletonLoader from "@/components/SkeletonLoader.vue"

import { Field, DataDefinitionWithFields, FieldInstance, TreeNode, DataSourceSetupDetails } from "@/types"

import api from "@/services/api"
import apiDefaultCache from "@/services/apiDefaultCache"

import Tree from "@/components/Config/Tree/Tree.vue"
import TreeNew from "@/components/Config/Tree/TreeNew.vue"
import { TreeNodeVisibilityResult } from "@/types/treeNodeVisibilityResult"
import { CurrentUser } from "@/types/user"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { DATA_SOURCE_ITEM_LIMIT_SMALL, DATA_SOURCE_ITEM_LIMIT_LARGE } from "@/constants/dataSource"

const setup = namespace("setup")

@Component({
	components: {
		"help-icon": HelpIcon,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-modal": DataModal,
		"details-modal": DataDetailsModal,
		"user-image": UserImage,
		"data-select": DataSourceSelect,
		"skeleton": SkeletonLoader,
		"tree": Tree,
		"treeNew": TreeNew
	}
})
export default class SourceSearch extends Vue {
	$refs!: {
		dataModal: DataModal,
		detailsModal: DataDetailsModal
	}

	@setup.Action
	public loadTeams!: () => void

	@setup.Getter("getTeams")
	public teams!: any[]

	@Getter("getMe") public me!: CurrentUser

	@ProvideReactive("treeVisibility") visibility: { [key: string]: boolean } = {};

	@setup.Getter("getDataSourcesById")
	public dataSourcesById!: any;

	@setup.Getter("getDefinitionsById")
	public definitionsById!: any;

	@Getter("getRecordName") getRecordName!: string;

	@Getter("getUseScalingComponent") useScalingComponent! : boolean;

	@Getter("getIsSuiteEnabled") isSuiteEnabled! : boolean;

	@Getter("getIsFeatureEnabled") getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	tenantFeatures = TenantFeatures

	@Prop({ default: null })
	public dataSource!: DataSourceSetupDetails;

	@Prop({ default: "" })
	public helpText!: string;

	public definition: DataDefinitionWithFields | null = null;
	public definitionFields: Field[] = [];

	public displayAll: boolean = false;
	public filter: string = "";

	public loading: boolean = true;

	public searching: boolean = false;

	public results: any[] | null = null;
	public searchResults: any[] = [];

	public searchField: any = null;
	public searchTerm: string = "";

	private uploadOpen: boolean = false;
	private dragging: boolean = false;
	private uploadError: any = null;

	// Tree layout
	private treeActiveItemId: string | null = null;
	private treeCreateItemParentId: string | null = null;
	private treeItemBeingDeletedHasChildren: boolean = false;
	private showTopLevelOnly: boolean = true;
	private flatTree: TreeNode<any>[] = [];

	private filterResultsDebounceTimeout: NodeJS.Timeout | null = null;
	private filterSearchTerm: string = "";

	private get shouldShowTopLevelOnly () {
		return this.showTopLevelOnly
	}

	private get useLargeTree () :boolean {
		// there will be logic here to determine if we should be using the large tree
		return this.useScalingComponent
	}

	private get dataSourceItemLimit () : number {
		const featureEnabled = (this.me && this.me.tenantEnabledFeatures && this.me.tenantEnabledFeatures.includes(TenantFeatures.DataSourceItemLimit500)) || false
		return featureEnabled ? DATA_SOURCE_ITEM_LIMIT_LARGE : DATA_SOURCE_ITEM_LIMIT_SMALL
	}

	public async mounted () {
		await this.onDataSourceChanged()
		await this.loadTeams()

		this.loading = false
	}

	@Watch("dataSource")
	async onDataSourceChanged () {
		this.results = null
		if (this.dataSource) {
			this.loading = true

			try {
				await this.definitionChanged(this.dataSource.definitionId)

				if (this.dataSource.getItems) {
					this.displayAll = true
					this.results = await this.dataSource.getItems()
				} else {
					let totalItems = this.dataSource.itemCount

					if (this.dataSource.includedLists) {
						totalItems += this.dataSource.includedLists
							.reduce((total: number, list: any) => total + list.itemCount, 0)
					}

					if (totalItems < this.dataSourceItemLimit) {
						this.displayAll = true
						this.results = await api.dataSourceGetAll(this.dataSource.id!, true)
					}
				}

				this.setTreeVisibility()
			} finally {
				this.loading = false
			}
		}
	}

	public get definitionId () {
		if (!this.dataSource) {
			return null
		}

		return this.dataSource.definitionId
	}

	public get canEdit (): boolean {
		return this.dataSource &&
			(!this.dataSource.editPermissions ||
			this.dataSource.editPermissions.length === 0 ||
			(this.me && this.me.teams.some(team => this.dataSource.editPermissions.some(teamId => team.id === teamId))))
	}

	public get canCreate (): boolean {
		return !(this.dataSource.id === "areas" && this.isSuiteEnabled)
	}

	public get canDelete (): boolean {
		return !(this.dataSource.id === "areas" && this.isSuiteEnabled)
	}

	@Watch("definitionId")
	async definitionChanged (definitionId: string | null) {
		if (definitionId) {
			this.definition = await api.definitionDetails(definitionId)
			this.definitionFields = this.definition.fields.map((field: FieldInstance) => field.field)
		} else {
			this.definition = null
			this.definitionFields = []
		}
	}

	@Watch("filter")
	filterChanged () {
		this.loading = true
		try {
			if (!this.filter) {
				this.setTreeVisibility()
				this.showTopLevelOnly = true

				return
			}
			if (this.useLargeTree) {
				let filterMatches = treeFilter.methods.getFilterMatches(this.flatTree, this.filter)
				this.searchResults = treeFilter.methods.organiseFilterResults(filterMatches)
				this.showTopLevelOnly = false
			} else {
				this.filterOriginalTree()
			}
		} finally {
			this.loading = false
		}
	}

	private filterOriginalTree (): void {
		if (this.flatTree) {
			const lowerFilter = this.filter.toLowerCase()
			const filterMatches: TreeNode<any>[] = []

			// Set visibility on matched list
			this.flatTree.forEach(item => {
				this.visibility[item.node.id] = this.filter === "" || item.node.title.toLowerCase().indexOf(lowerFilter) > -1
				if (this.visibility[item.node.id]) {
					filterMatches.push(item)
				}
			})

			if (this.filter !== "") {
				// Show parent visibility back to root
				const makeParentVisible = (item: TreeNode<any>) => {
					this.visibility[item.node.id] = true
					if (item.parent) {
						makeParentVisible(item.parent)
					}
				}
				filterMatches.forEach(makeParentVisible)
			}
		}
	}

	get modalColumns () {
		if (!this.definition || !this.definition.fields) {
			return [{
				key: "label",
				label: "Text",
				dataType: "text",
				dataSourceId: null,
				errors: (item: any) => item.label ? null : "Please enter text"
			},
			{
				key: "fields.permissions",
				label: "View Permissions",
				dataType: "list-multiple",
				options: this.teams,
				description: "Roles that can view this data source item",
				placeholder: "Any"
			}]
		}

		const specialFields = this.dataSource.extraFields ? this.dataSource.extraFields : []
		const fields = this.definition.fields.sortBy("order")
			.map((field: FieldInstance) => {
				return {
					key: "fields." + field.field.id,
					label: field.field.title,
					dataType: field.field.dataType,
					dataSourceId: field.field.dataSourceId,
					extensions: field.field.extensions,
					section: field.section,
					readonly: field.field.displayReadOnly,
					errors: (item: any) => !field.requiredRecord || (item.fields && item.fields[field.field.id!]) ? null : "Please enter " + field.field.title
				}
			})

		return [
			...specialFields,
			...fields,
			{
				key: "fields.permissions",
				label: "View Permissions",
				dataType: "list-multiple",
				options: this.teams,
				description: "Roles that can view this data source item",
				placeholder: "Any"
			}
		]
	}

	get searchResultsColumns () {
		if (!this.definition) {
			return [{
				key: "label",
				label: "Text"
			}, {
				key: "cases",
				label: ""
			}]
		}

		this.definition.fields.forEach((field: FieldInstance) => {
			if (this.definition!.sections) {
				const sectionOrder = this.definition!.sections.indexOf(field.section)
				field.sectionOrder = sectionOrder === -1 ? 99999 : sectionOrder
			}
		})

		const fieldColumns = this.definition.fields
			.filter((c: FieldInstance) => c.includeInTable || (c.field && this.searchField && c.field.id === this.searchField.id))
			.sortByThen({ key: "sectionOrder" }, { thenKey: "order" })
			.map((field: FieldInstance) => {
				return {
					key: "fields." + (field.field.dataType === "list" ? field.field.id + "-label" : field.field.id),
					dataType: field.field.dataType,
					calculationType: field.field.dataType === "calculated" && field.field.extensions ? field.field.extensions.calculationType : null,
					extensions: field.field.extensions,
					label: field.field.title
				}
			})

		const specialFields = this.dataSource.extraFields ? this.dataSource.extraFields : []

		return [
			...specialFields,
			...fieldColumns, {
				key: "cases",
				label: ""
			}
		]
	}

	private get hasUploadError (): boolean {
		return !!this.uploadError
	}

	private set hasUploadError (value: boolean) {
		if (value && !this.uploadError) {
			this.uploadError = true
		} else if (!value && this.uploadError) {
			this.uploadError = null
		}
	}

	async searchItems () {
		this.results = await api.dataSourceSearch(this.dataSource.id!,
			this.searchField ? this.searchField.id : null, this.searchTerm)
	}

	createItem () {
		if (this.dataSource.createItemModalTitle) {
			this.$refs.dataModal.title = this.dataSource.createItemModalTitle
		}

		this.$refs.dataModal.open({
			dataSourceId: this.dataSource.id,
			fields: {}
		})
	}

	async saveItem (saveItem: any) {
		this.loading = true

		const itemBeingUpdated = !!saveItem.id

		if (this.dataSource.treeLayout) {
			if (saveItem.parentId === undefined) {
				if (this.treeCreateItemParentId != null) {
					saveItem.parentId = this.treeCreateItemParentId
					this.treeCreateItemParentId = null
				}
			}
		}

		// If the dataSource does exist, update the data source, otherwise create it
		const newItem = this.dataSource.save
			? await this.dataSource.save(saveItem)
			: await api.dataSourceCreateItem(saveItem)

		apiDefaultCache.clear()

		if (!newItem) {
			this.loading = false
			return
		}

		if (this.dataSource.treeLayout) {
			if (!this.useLargeTree) {
				Vue.set(this.visibility, newItem.id, this.filter === "" || newItem.title.toLowerCase().indexof(this.filter.toLowerCase) > -1)
			}

			const newNode: TreeNode<any> = {
				node: newItem,
				children: null,
				visible: true
			}

			if (!this.flatTree.find((item: any) => saveItem.id === item.node.id)) {
				this.flatTree.push(newNode)
			}

			if (newItem.parentId) {
				const parent = this.flatTree.find(i => i.node.id === newItem.parentId)
				if (parent) {
					newItem.parent = newItem
					if (!parent!.children) {
						Vue.set(parent, "children", [])
					}

					const existingItem = parent!.children!.find((item: any) => newItem.id === item.node.id)
					if (existingItem) {
						existingItem.node.title = newItem.title
					} else {
						parent!.children!.push(newNode)
					}
				}
			} else {
				const existingItem = this.searchResults!.find((item: any) => newItem.id === item.node.id)
				if (existingItem) {
					existingItem.node.title = newItem.title
				} else {
					this.searchResults!.push(newNode)
				}
			}
		} else {
			if (this.displayAll) {
				if (this.dataSource.getItems) {
					this.results = await this.dataSource.getItems()
				} else {
					this.results = await api.dataSourceGetAll(this.dataSource.id!, true)
				}
			} else if (saveItem.id && this.results) {
				const searchResultItem = this.results.find((item: any) => newItem.id === item.id)
				if (searchResultItem) {
					Object.keys(newItem).forEach((key: string) => {
						Vue.set(searchResultItem, key, newItem[key])
					})
				}
			}
		}

		this.loading = false
	}

	async deleteItem (deleteItem: any) {
		this.loading = true

		if (this.dataSource.delete) {
			try {
				await this.dataSource.delete(deleteItem)
			} catch {
				this.loading = false
				return
			}
		} else {
			await api.dataSourceItemDelete(deleteItem)
		}

		if (this.dataSource.treeLayout) {
			if (!this.useLargeTree && this.visibility[deleteItem.id] !== undefined) {
				delete this.visibility[deleteItem.id]
			}

			const flatIndex = this.flatTree.findIndex((item: any) => deleteItem.id === item.node.id)
			if (flatIndex > -1) {
				this.flatTree.splice(flatIndex, 1)
			}

			if (deleteItem.parentId) {
				const parent = this.flatTree.find(i => i.node.id === deleteItem.parentId)
				if (parent) {
					if (parent.children) {
						const parentIndex = parent.children.findIndex((item: any) => deleteItem.id === item.node.id)
						if (parentIndex > -1) {
							parent.children.splice(parentIndex, 1)
						}
					}
				}
			} else {
				const resultIndex = this.searchResults!.findIndex((item: any) => deleteItem.id === item.node.id)
				if (resultIndex > -1) {
					this.searchResults!.splice(resultIndex, 1)
				}
			}
		} else {
			if (deleteItem.id && this.searchResults) {
				this.searchResults.removeWhere((item: any) => item.id === deleteItem.id)
			}
		}

		this.setTreeVisibility()

		this.loading = false
	}

	async leaveSearch () {
		this.$emit("close")
	}

	public async rowClick (item: any) {
		this.openItemDetailsModal(item)
	}

	private async openItemDetailsModal (item: any) {
		const itemClone = this.dataSource.itemDetails ? await this.dataSource.itemDetails(item) : cloneDeep(item)

		let itemDefinitionColumns = null

		if (item.dataSourceId && item.dataSourceId !== this.dataSource.id) {
			const dataSource = this.dataSourcesById[item.dataSourceId]

			if (dataSource.definitionId !== this.dataSource.definitionId) {
				const definition = await api.definitionDetails(dataSource.definitionId)

				itemDefinitionColumns = definition.fields.map((field: FieldInstance) => {
					return {
						key: "fields." + field.field.id,
						label: field.field.title,
						dataType: field.field.dataType,
						dataSourceId: field.field.dataSourceId,
						errors: (item: any) => !field.requiredRecord || item.fields[field.field.id!] ? null : "Please enter " + field.field.title
					}
				})
			}
		}

		this.$refs.dataModal.open(itemClone, itemDefinitionColumns)
	}

	public async viewCases (item: any) {
		const dataSource = this.dataSourcesById[item.dataSourceId]

		this.$refs.detailsModal.open({
			data: item,
			definition: dataSource.definitionId ? await api.definitionDetails(dataSource.definitionId) : null,
			showDataForm: false,
			autoSearch: true
		})
	}

	public async showAllResults () {
		// optionally set loading animation here
		this.displayAll = true
		this.results = await api.dataSourceGetAll(this.dataSource.id!, true)
	}

	public startEdit () {
		this.$emit("editSource", this.dataSource)
	}

	public itemValue (item: any, columnKey: string): any {
		if (!item) {
			return null
		}

		const dotIndex = columnKey.indexOf(".")
		if (dotIndex > -1) {
			const topLevel = columnKey.substring(0, dotIndex)
			const rest = columnKey.substring(dotIndex + 1)
			return this.itemValue(item[topLevel], rest)
		}

		return item[columnKey]
	}

	private downloadTemplate (): void {
		let downloadUrl = api.baseUrl + "dataSource/upload/" + this.dataSource.id

		let link = document.createElement("a")
		link.href = downloadUrl
		link.download = downloadUrl
		link.click()
	}

	private async uploadFiles (event: any): Promise<void> {
		this.dragging = false
		this.uploadError = null

		try {
			let files = event && event.target ? (event.target.files || (event.dataTransfer ? event.dataTransfer.files : [])) as any[] : []

			const uploadFormData = new FormData()
			files.forEach(file => {
				uploadFormData.append("file", file)
			})

			const newItems = await api.dataSourceUploadCsv(this.dataSource.id!, uploadFormData, () => {
			})

			if (this.displayAll) {
				if (!this.results) {
					this.results = []
				}

				newItems.forEach((item: any) => {
					this.results!.push(item)
					this.searchResults!.push(item)
				})
			} else {
				this.results = newItems
				this.searchResults = newItems
			}

			this.uploadOpen = false
		} catch (ex) {
			this.uploadError = ex
		}
	}

	private setTreeActiveItem (itemId: string) {
		this.treeActiveItemId = itemId
		if (this.dataSource.searchResults) {
			this.treeItemBeingDeletedHasChildren = this.dataSource.searchResults.some((item: TreeNode<any>) => {
				return this.treeItemHasChildren(item, itemId)
			})
		}
	}

	private treeItemHasChildren (item: TreeNode<any>, itemId: string): boolean {
		if (item.node.id === itemId && (item.children && item.children.length > 0)) {
			return true
		} else {
			if (item.children && item.children.length > 0) {
				return item.children.some(item => {
					return this.treeItemHasChildren(item, itemId)
				})
			}
		}
		return false
	}

	private treeItemSelected (item: any) {
		this.treeCreateItemParentId = null

		if (this.dataSource.treeItemSelectedModalTitle) {
			this.$refs.dataModal.title = this.dataSource.treeItemSelectedModalTitle
		}

		this.openItemDetailsModal(item)
	}

	private createTreeItem (item: any) {
		if (item.node.id) {
			this.treeCreateItemParentId = item.node.id
		}

		if (this.dataSource.treeCreateItemModalTitle) {
			this.$refs.dataModal.title = this.dataSource.treeCreateItemModalTitle
		}

		this.$refs.dataModal.open({
			dataSourceId: this.dataSource.id,
			fields: {}
		})
	}

	private setTreeVisibility () {
		if (!this.dataSource.treeLayout) {
			return
		}

		if (this.searchResults && this.results) {
			// clear existing results
			this.flatTree = []
			this.searchResults = []

			// set visibility based on the tree that should be used
			if (this.useLargeTree) {
				this.setLargeTreeVisibility()
			} else {
				this.results.forEach(this.setTreeChildrenVisibility)
				this.searchResults = this.results
			}
		}
	}

	private setLargeTreeVisibility (): void {
		if (!this.results) {
			return
		}

		let treeNodeVisibilityResults: TreeNodeVisibilityResult = treeFilter.methods.setTreeVisibility(this.results)
		this.searchResults = treeNodeVisibilityResults.treeStructure
		this.flatTree = treeNodeVisibilityResults.flatList
	}

	private setTreeChildrenVisibility (item: TreeNode<any>) {
		this.flatTree.push(item)
		Vue.set(this.visibility, item.node.id, true)

		if (item.children) {
			item.children.forEach(childItem => {
				childItem.parent = item
				this.setTreeChildrenVisibility(childItem)
			})
		}
	}

	public async filterResults (): Promise<void> {
		if (this.filterResultsDebounceTimeout) {
			clearTimeout(this.filterResultsDebounceTimeout)
		}

		if (this.filterSearchTerm === "") {
			this.filter = this.filterSearchTerm
		} else {
			this.filterResultsDebounceTimeout = setTimeout(async () => {
				this.filter = this.filterSearchTerm
			}, 1000)
		}
	}
}
